import React from "react";
import {
  Box,
  Drawer,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import TextElementEditor from "./elementEditors/TextElementEditor";
import ButtonElementEditor from "./elementEditors/ButtonElementEditor";
import ImageElementEditor from "./elementEditors/ImageElementEditor";
import CollectionItemEditor from "./elementEditors/CollectionItemEditor";
import CollectionElementEditor from "./elementEditors/CollectionElementEditor";
import NavbarMobileElementEditor from "./elementEditors/NavbarMobileElementEditor";
import SectionShellEditor from "./elementEditors/SectionShellEditor";
import CardSystemEditor from "./elementEditors/CardSystemEditor";
import CategoriesElementEditor from "./elementEditors/CategoriesElementEditor";
import ContactElementEditor from "./elementEditors/ContactElementEditor";
import FooterElementEditor from "./elementEditors/FooterElementEditor";
import FloatingWhatsappElementEditor from "./elementEditors/FloatingWhatsappElementEditor";
import CustomEmbedElementEditor from "./elementEditors/CustomEmbedElementEditor";
import { resolveElementDescriptor } from "./elementEditing";
import { SEGMENT_LABELS } from "./segmentVisualStyle";

export default function LandingElementInspector({
  open,
  selectedElement,
  shellSectionId = "",
  form,
  saving,
  onClose,
  onCommit,
  onToggleSection,
  onUploadImage,
  onRemoveImage,
  onOpenGlobalPalette,
}) {
  const mobile = useMediaQuery("(max-width:760px)");
  const desktopEditor = useMediaQuery("(min-width:1101px)");
  const descriptor = React.useMemo(
    () => (selectedElement ? resolveElementDescriptor(form, selectedElement) : null),
    [form, selectedElement]
  );
  const sectionId = descriptor?.sectionId || shellSectionId;
  const sectionLabel = descriptor?.definition?.label || SEGMENT_LABELS[descriptor?.sectionType || sectionId] || "Sección";
  const elementLabel = descriptor?.label || "Sección";
  const isShell = !selectedElement && Boolean(shellSectionId);

  return (
    <Drawer
      anchor={mobile ? "bottom" : "right"}
      variant={desktopEditor ? "persistent" : "temporary"}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: mobile
          ? { maxHeight: "82vh", borderRadius: "22px 22px 0 0" }
          : desktopEditor
            ? { width: 410, top: 64, bottom: 139, height: "auto" }
            : { width: 410, top: 64, height: "calc(100% - 64px)" },
      }}
    >
      <Stack sx={{ minHeight: 0, height: "100%" }}>
        <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ px: 2, py: 1.6, borderBottom: "1px solid rgba(25,36,47,.09)" }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)", fontWeight: 850 }}>
              {sectionLabel} &gt; {isShell ? "Sección" : elementLabel}
            </Typography>
            <Typography sx={{ mt: 0.25, fontWeight: 950, color: "#19242F" }}>
              {isShell ? "Ajustes de la sección" : `Editar ${String(elementLabel || "elemento").toLowerCase()}`}
            </Typography>
          </Box>
          <IconButton aria-label="Cerrar editor" onClick={onClose}><CloseRoundedIcon /></IconButton>
        </Stack>

        <Box sx={{ p: 2, overflowY: "auto", flex: 1 }}>
          {isShell ? (
            <SectionShellEditor sectionId={shellSectionId} form={form} disabled={saving} onCommit={onCommit} onUploadImage={onUploadImage} onRemoveImage={onRemoveImage} onOpenGlobalPalette={onOpenGlobalPalette} />
          ) : descriptor?.kind === "button" ? (
            <ButtonElementEditor descriptor={descriptor} form={form} disabled={saving} onCommit={onCommit} />
          ) : descriptor?.kind === "image" ? (
            <ImageElementEditor descriptor={descriptor} form={form} disabled={saving} onCommit={onCommit} onUploadImage={onUploadImage} onRemoveImage={onRemoveImage} />
          ) : (descriptor?.kind === "collection-item" || descriptor?.kind === "collection-manager") && ["catalog.brands.items", "highlights"].includes(descriptor?.property?.path || descriptor?.collection?.property?.path) ? (
            <CollectionElementEditor descriptor={descriptor} form={form} disabled={saving} onCommit={onCommit} onUploadImage={onUploadImage} onRemoveImage={onRemoveImage} />
          ) : descriptor?.kind === "collection-item" || descriptor?.kind === "collection-manager" ? (
            <CollectionItemEditor descriptor={descriptor} form={form} disabled={saving} onCommit={onCommit} onUploadImage={onUploadImage} onRemoveImage={onRemoveImage} />
          ) : descriptor?.kind === "navbar-mobile" ? (
            <NavbarMobileElementEditor descriptor={descriptor} form={form} disabled={saving} onCommit={onCommit} />
          ) : descriptor?.kind === "card-system" ? (
            <CardSystemEditor descriptor={descriptor} form={form} disabled={saving} onCommit={onCommit} onUploadImage={onUploadImage} onRemoveImage={onRemoveImage} />
          ) : descriptor?.kind === "catalog-derived" || descriptor?.kind === "catalog-item-naming" ? (
            <CategoriesElementEditor
              descriptor={descriptor}
              form={form}
              disabled={saving}
              onCommit={onCommit}
              onToggleSection={onToggleSection}
              onUploadImage={onUploadImage}
              onRemoveImage={onRemoveImage}
            />
          ) : descriptor?.kind === "footer-group" ? (
            <FooterElementEditor
              descriptor={descriptor}
              form={form}
              disabled={saving}
              onCommit={onCommit}
            />
          ) : descriptor?.kind === "floating-whatsapp" ? (
            <FloatingWhatsappElementEditor
              descriptor={descriptor}
              form={form}
              disabled={saving}
              onCommit={onCommit}
            />
          ) : descriptor?.kind === "custom-embed" ? (
            <CustomEmbedElementEditor
              descriptor={descriptor}
              form={form}
              disabled={saving}
              onCommit={onCommit}
            />
          ) : descriptor?.kind === "contact-group" ? (
            <ContactElementEditor
              descriptor={descriptor}
              form={form}
              disabled={saving}
              onCommit={onCommit}
              onUploadImage={onUploadImage}
              onRemoveImage={onRemoveImage}
            />
          ) : descriptor?.kind === "text" && descriptor?.field ? (
            <TextElementEditor descriptor={descriptor} form={form} disabled={saving} onCommit={onCommit} />
          ) : (
            <Typography variant="body2" sx={{ color: "rgba(25,36,47,.7)" }}>
              Este componente usa la misma selección directa, pero sus controles específicos se habilitan al migrar su sección. No se creó un segundo campo de configuración.
            </Typography>
          )}
        </Box>
      </Stack>
    </Drawer>
  );
}
